import type { Mark } from "./grades";
import type { Bands } from "./lanes";
import { mergeNetworks, paperKey, type PreviewPaper, type SeedNetwork } from "./preview";

/** ③ calls a sample off-topic when at least OFF_TOPIC_MIN graded papers score
 *  under OFF_TOPIC_SCORE without a ✕ (a ✕ on a low score means "should be
 *  higher" — a grading problem, which Sharpen handles). */
export const OFF_TOPIC_SCORE = 40;
export const OFF_TOPIC_MIN = 4;

/** What ③ hands to ②: the seed that brought most of the off-topic papers. */
export interface RefindBlame { key: string; label: string; offTopic: number; total: number }

export function offTopicKeys(grades: Map<string, { score: number }>, marks: Map<string, Mark>): string[] {
  return [...grades].filter(([k, g]) => g.score < OFF_TOPIC_SCORE && marks.get(k) !== "wrong").map(([k]) => k);
}

/** The seed whose network holds the most off-topic papers (at least 2); ties → first. */
export function blameSeed(off: string[], seeds: { key: string; net: SeedNetwork }[]): { key: string; count: number } | null {
  let best: { key: string; count: number } | null = null;
  for (const s of seeds) {
    const keys = mergeNetworks([s.net]);
    const count = off.filter((k) => keys.has(k)).length;
    if (count >= 2 && (!best || count > best.count)) best = { key: s.key, count };
  }
  return best;
}

/** With ②'s lanes checked, ③'s sample is drawn 3 hit / 3 borderline / 2 miss,
 *  so its grades can't show an off-topic network. Judge the network instead:
 *  off when at least OFF_TOPIC_SHARE of the ticked seeds' checked papers were
 *  banded off-topic or tangential (0–1) by the quick check. */
export const OFF_TOPIC_SHARE = 0.5;
export const OFF_TOPIC_MIN_CHECKED = 8;

export interface LaneOffTopic { off: number; checked: number; blame: { key: string; count: number } | null }

export function laneOffTopic(
  seeds: { key: string; pick: PreviewPaper[] }[], bands: Bands,
): LaneOffTopic | null {
  const offBy = seeds.map((s) => ({
    key: s.key,
    off: new Set(s.pick.map((x) => paperKey(x.paper)).filter((k) => { const b = bands[k]; return b === 0 || b === 1; })),
  }));
  const all = new Set(seeds.flatMap((s) => s.pick.map((x) => paperKey(x.paper))).filter((k) => bands[k] != null));
  const off = new Set(offBy.flatMap((s) => [...s.off]));
  if (all.size < OFF_TOPIC_MIN_CHECKED || off.size < all.size * OFF_TOPIC_SHARE) return null;
  let blame: { key: string; count: number } | null = null;
  for (const s of offBy) if (s.off.size >= 2 && (!blame || s.off.size > blame.count)) blame = { key: s.key, count: s.off.size };
  return { off: off.size, checked: all.size, blame };
}
